<div class="organization-setup-shell" :style="branding_style">
  <div v-if="saving_profile" class="profile-switch-loader-backdrop">
    <div class="profile-switch-loader" role="status" aria-live="polite">
      <div class="profile-switch-loader-icon">
        <i class="bi bi-person-check-fill"></i>
      </div>
      <p class="profile-switch-loader-title">Saving profile</p>
      <p class="profile-switch-loader-text">Updating your organisation workspace...</p>
      <div class="profile-switch-loader-dots" aria-hidden="true">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
  </div>

  <HomeNavBar />

  <main class="p-2">
    <header class="d-flex gap-2 align-items-start mb-3">
      <button
        @click="$router.back()"
        class="btn btn-light rounded-circle p-2 d-flex align-items-center justify-content-center back-btn"
      >
        <i class="bi bi-chevron-left fs-5"></i>
      </button>

      <h4 class="fw-bold mb-0">Setup your organisation profile</h4>
    </header>

    <section class="organization-setup-panel">
      <div class="row g-3 mb-3">
        <div class="col-md-4">
          <div
            v-if="form.logo_preview"
            class="organization-setup-logo position-relative"
          >
            <img :src="form.logo_preview" alt="Organisation logo" @error="clear_broken_logo_preview" />

            <label
              class="btn btn-sm btn-primary rounded-pill position-absolute bottom-0 end-0"
            >
              <i class="bi bi-pencil"></i>
              <input
                type="file"
                accept="image/*"
                class="d-none"
                @change="preview_file($event, 'logo_preview')"
              />
            </label>
          </div>

          <label
            v-else
            class="organization-logo-dropzone d-flex flex-column align-items-center justify-content-center text-center p-4 pointer"
            :class="isDraggingLogo ? 'border-primary bg-primary-subtle' : ''"
            @dragover.prevent="isDraggingLogo = true"
            @dragenter.prevent="isDraggingLogo = true"
            @dragleave.prevent="isDraggingLogo = false"
            @drop.prevent="handle_logo_drop"
          >
            <i class="bi bi-cloud-arrow-up-fill text-primary fs-1 mb-3"></i>

            <h6 class="fw-bold mb-2">Upload Organisation Logo</h6>

            <p class="text-muted small mb-0">
              Drag & drop your logo here<br />
              or click to browse
            </p>

            <input
              type="file"
              accept="image/*"
              class="d-none"
              @change="preview_file($event, 'logo_preview')"
            />
          </label>
        </div>

        <div class="col-md-8">
          <div class="mb-2">
            <label class="form-label text-sm fw-500" for="organization_name">
              Organisation Name
            </label>
            <input
              id="organization_name"
              class="form-control rounded text-sm p-2"
              v-model="form.organization_name"
              placeholder="Enter organisation name"
            />
          </div>

          <div class="mb-2">
            <label class="form-label text-sm fw-500" for="type_of_organization">
              Organisation Type
            </label>
            <input
              id="type_of_organization"
              class="form-control rounded text-sm p-2"
              v-model="form.type_of_organization"
              placeholder="Enter organisation type"
              readonly
            />
          </div>

          <div class="mb-2">
            <label class="form-label text-sm fw-500" for="description">
              Description
            </label>
            <textarea
              id="description"
              rows="5"
              class="form-control rounded-4 text-sm p-2"
              v-model="form.description"
              placeholder="Describe your organisation"
            ></textarea>
          </div>

          <div class="mb-2">
            <label class="form-label text-sm fw-500" for="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              class="form-control rounded text-sm p-2"
              v-model="form.email"
              placeholder="Enter organisation email"
            />
          </div>

          <div class="mb-2">
            <label class="form-label text-sm fw-500" for="tell_number">
              Telephone
            </label>
            <input
              id="tell_number"
              class="form-control rounded text-sm p-2"
              v-model="form.tell_number"
              placeholder="Enter telephone number"
            />
          </div>

          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label text-sm fw-500" for="organization_street_address">
                Street Address
              </label>
              <input
                id="organization_street_address"
                ref="streetAddressInput"
                class="form-control rounded text-sm p-2"
                v-model="address_list.street_address"
                placeholder="Street address"
                @input="sync_location_from_address"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label text-sm fw-500" for="organization_suburb">
                Suburb / Local Area
              </label>
              <input
                id="organization_suburb"
                class="form-control rounded text-sm p-2"
                v-model="address_list.suburb"
                placeholder="Suburb or local area"
                @input="sync_location_from_address"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label text-sm fw-500" for="organization_town">
                Town / City
              </label>
              <input
                id="organization_town"
                class="form-control rounded text-sm p-2"
                v-model="address_list.town"
                placeholder="Town or city"
                @input="sync_location_from_address"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label text-sm fw-500" for="organization_province">
                Province
              </label>
              <input
                id="organization_province"
                class="form-control rounded text-sm p-2"
                v-model="address_list.province"
                placeholder="Province"
                @input="sync_location_from_address"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label text-sm fw-500" for="organization_postal_code">
                Postal Code
              </label>
              <input
                id="organization_postal_code"
                class="form-control rounded text-sm p-2"
                v-model="address_list.postal_code"
                placeholder="Postal code"
                @input="sync_location_from_address"
              />
            </div>
          </div>
        </div>
      </div>

      <section class="organization-setup-cover d-none">
        <img
          v-if="form.cover_preview"
          :src="form.cover_preview"
          alt="Organisation cover"
        />

        <div v-else class="organization-setup-cover-empty">
          <i class="bi bi-image"></i>
        </div>

        <label class="btn btn-light rounded text-xs organization-setup-upload">
          Cover Image
          <input
            type="file"
            accept="image/*"
            @change="preview_file($event, 'cover_preview')"
          />
        </label>
      </section>

      <div class="organization-brand-header">
        <div>
          <h2>Organisation Branding</h2>
          <p>
            Set the colours and logo that represent your organisation across the platform.
          </p>
        </div>
      </div>

      <div class="organization-brand-grid">
        <div class="organization-brand-controls">
          <div class="organization-color-field">
            <label class="form-label text-sm fw-500" for="primary_color">
              Primary Colour
            </label>
            <div class="organization-color-input">
              <input id="primary_color" type="color" v-model="form.primary_color" />
              <input
                class="form-control rounded text-sm p-2"
                v-model="form.primary_color"
                placeholder="#fed700"
              />
            </div>
          </div>

          <div class="organization-color-field">
            <label class="form-label text-sm fw-500" for="secondary_color">
              Secondary Colour
            </label>
            <div class="organization-color-input">
              <input id="secondary_color" type="color" v-model="form.secondary_color" />
              <input
                class="form-control rounded text-sm p-2"
                v-model="form.secondary_color"
                placeholder="#43285b"
              />
            </div>
          </div>

          <div class="organization-color-field">
            <label class="form-label text-sm fw-500" for="tertiary_color">
              Tertiary Colour
            </label>
            <div class="organization-color-input">
              <input id="tertiary_color" type="color" v-model="form.tertiary_color" />
              <input
                class="form-control rounded text-sm p-2"
                v-model="form.tertiary_color"
                placeholder="#365b6d"
              />
            </div>
          </div>
        </div>

        <div
          class="organization-brand-preview"
          :style="{
            '--preview-secondary': form.secondary_color,
            '--preview-tertiary': form.tertiary_color
          }"
        >
          <div class="organization-brand-preview-nav">
            <div class="organization-brand-preview-logo">
              <img
                v-if="form.logo_preview"
                :src="form.logo_preview"
                alt="Brand logo preview"
                @error="clear_broken_logo_preview"
              />
              <span v-else>
                {{ (form.organization_name || 'O').charAt(0).toUpperCase() }}
              </span>
            </div>

            <span>{{ form.organization_name || 'Organisation' }}</span>
          </div>

          <div class="organization-brand-preview-body">
            <h3>Brand Preview</h3>
            <p>Primary, secondary, tertiary colours and logo preview.</p>
            <button type="button">Primary Action</button>
          </div>
        </div>
      </div>

      <div class="organization-brand-actions">
        <button
          class="btn btn-primary rounded text-xs px-4"
          @click="save_branding"
        >
          Save changes
        </button>

        <button
          class="btn btn-outline-secondary text-xs rounded px-4"
          @click="reset_branding"
        >
          Reset
        </button>
      </div>
    </section>
  </main>
</div>
